import {
  AfterViewInit,
  Directive,
  DoCheck,
  Host,
  Optional,
  Renderer2,
  Self,
  ViewContainerRef,
  OnDestroy,
} from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Directive({
  selector: "[libErPagination]",
  standalone: true,
})
export class PaginatorDirective implements DoCheck, AfterViewInit, OnDestroy {
  private currentPage: number;
  private pageGapTxt: string[];
  private rangeStart!: number;
  private rangeEnd!: number;
  private buttons: HTMLButtonElement[] = [];
  private showTotalPages: number;
  private checkPage: number[];
  private isCompact = false;
  private destroy$ = new Subject<void>();

  constructor(
    @Host() @Self() @Optional() private readonly matPag: MatPaginator,
    private readonly ViewContainer: ViewContainerRef,
    private readonly renderer: Renderer2,
    private readonly bo: BreakpointObserver
  ) {
    this.currentPage = 1;
    this.pageGapTxt = ["•••", "---"];
    this.showTotalPages = 3;
    this.checkPage = [0, 0, 0];

    // Re-render when paginator emits
    this.matPag.page
      .pipe(takeUntil(this.destroy$))
      .subscribe((paginator: PageEvent) => {
        this.currentPage = paginator.pageIndex;
        this.matPag.pageIndex = paginator.pageIndex;
        this.initPageRange();
      });

    // Compact mode on small screens
    this.bo
      .observe([Breakpoints.Handset, "(max-width: 600px)"])
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        const anyMatch = Object.values(state.breakpoints).some(Boolean);
        this.isCompact = !!anyMatch;
        this.initPageRange();
      });
  }

  ngDoCheck(): void {
    if (
      this.matPag?.length !== this.checkPage[0] ||
      this.matPag?.pageSize !== this.checkPage[1] ||
      this.matPag?.pageIndex !== this.checkPage[2]
    ) {
      const pageCount = this.matPag.getNumberOfPages();
      if (this.currentPage > pageCount && pageCount !== 0) {
        this.currentPage = 1;
        this.matPag.pageIndex = 0;
      }
      this.currentPage = this.matPag.pageIndex;
      this.initPageRange();
      this.checkPage = [
        this.matPag.length,
        this.matPag.pageSize,
        this.matPag.pageIndex,
      ];
    }
  }

  ngAfterViewInit(): void {
    this.rangeStart = 0;
    this.rangeEnd = this.showTotalPages - 1;
    this.initPageRange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildPageNumbers = () => {
    const totalPages = this.matPag.getNumberOfPages();

    const actionContainer =
      this.ViewContainer.element.nativeElement.querySelector(
        "div.mat-mdc-paginator-range-actions"
      );
    const nextPageNode = this.ViewContainer.element.nativeElement.querySelector(
      "button.mat-mdc-paginator-navigation-next"
    );
    const pageRange = this.ViewContainer.element.nativeElement.querySelector(
      "div.mat-mdc-paginator-range-label"
    );

    // Remove old custom buttons
    if (this.buttons.length > 0) {
      this.buttons.forEach((button) => {
        this.renderer.removeChild(actionContainer, button);
      });
      this.buttons = [];
    }

    // Base styling hooks
    if (pageRange) {
      this.renderer?.addClass(pageRange, "custom-paginator-counter");
    }
    if (actionContainer) {
      this.renderer?.addClass(actionContainer, "custom-paginator-container");
    }

    // Style native arrow buttons (Prev/Next)
    const nodeArray = actionContainer?.childNodes ?? [];
    setTimeout(() => {
      for (const node of nodeArray) {
        if (node.nodeName === "BUTTON") {
          if (node.innerHTML.length > 100 && node.disabled) {
            this.renderer.addClass(node, "custom-paginator-arrow-disabled");
            this.renderer.removeClass(node, "custom-paginator-arrow-enabled");
          } else if (node.innerHTML.length > 100 && !node.disabled) {
            this.renderer.addClass(node, "custom-paginator-arrow-enabled");
            this.renderer.removeClass(node, "custom-paginator-arrow-disabled");
          }
        }
      }
    });

    // ===== COMPACT MODE: show "current … last" =====
    // Examples:
    //  - pageIndex=0  -> "1 … N"
    //  - pageIndex=1  -> "2 … N"
    //  - pageIndex=2  -> "3 … N"
    //  - pageIndex=N-1-> "N" (no trailing ellipsis or duplicate last)
    if (this.isCompact) {
      if (totalPages > 0) {
        // Current page (selected/disabled by createButton switch-case)
        this.renderer.insertBefore(
          actionContainer,
          this.createButton(`${this.currentPage}`, this.matPag.pageIndex),
          nextPageNode
        );
      }

      // If not on the last page, show ellipsis and the last page
      if (totalPages > 1 && this.currentPage < totalPages - 1) {
        this.renderer.insertBefore(
          actionContainer,
          this.createButton(this.pageGapTxt[0], this.matPag.pageIndex),
          nextPageNode
        );
        this.renderer.insertBefore(
          actionContainer,
          this.createButton(`${totalPages - 1}`, this.matPag.pageIndex),
          nextPageNode
        );
      }

      return; // Skip full mode
    }

    // ===== FULL MODE (desktop/tablet) =====
    if (totalPages > 0) {
      this.renderer.insertBefore(
        actionContainer,
        this.createButton("0", this.matPag.pageIndex),
        nextPageNode
      );
    }

    // Early-window size uses showTotalPages (not +2)
    let page = this.showTotalPages;
    const pageDifference = totalPages - page;
    const startIndex = Math.max(this.currentPage - this.showTotalPages - 2, 1);

    const dots = [false, false];

    for (let index = startIndex; index < totalPages - 1; index = index + 1) {
      if (
        (index < page && this.currentPage <= this.showTotalPages) ||
        (index >= this.rangeStart && index <= this.rangeEnd) ||
        (this.currentPage > pageDifference && index >= pageDifference) ||
        totalPages < this.showTotalPages + page
      ) {
        this.renderer.insertBefore(
          actionContainer,
          this.createButton(`${index}`, this.matPag.pageIndex),
          nextPageNode
        );
      } else {
        if (index > this.rangeEnd && !dots[0]) {
          this.renderer.insertBefore(
            actionContainer,
            this.createButton(this.pageGapTxt[0], this.matPag.pageIndex),
            nextPageNode
          );
          dots[0] = true;
          break;
        }
        if (index < this.rangeEnd && !dots[1]) {
          this.renderer.insertBefore(
            actionContainer,
            this.createButton(this.pageGapTxt[1], this.matPag.pageIndex),
            nextPageNode
          );
          dots[1] = true;
        }
      }
    }

    if (totalPages > 1) {
      this.renderer.insertBefore(
        actionContainer,
        this.createButton(`${totalPages - 1}`, this.matPag.pageIndex),
        nextPageNode
      );
    }
  };

  private createButton(index: string, pageIndex: number): HTMLButtonElement {
    const linkBtn = this.renderer.createElement("button") as HTMLButtonElement;
    this.renderer.setAttribute(linkBtn, "class", "custom-paginator-page");
    this.renderer.addClass(linkBtn, "custom-paginator-page-enabled");
    if (index === this.pageGapTxt[0] || index === this.pageGapTxt[1]) {
      this.renderer.addClass(linkBtn, "custom-paginator-arrow-enabled");
    }
    const pagingTxt = isNaN(+index) ? this.pageGapTxt[0] : +index + 1;
    const text = this.renderer.createText(pagingTxt + "");
    this.renderer.addClass(linkBtn, "mat-custom-page");

    switch (index) {
      case `${pageIndex}`:
        this.renderer.setAttribute(linkBtn, "disabled", "disabled");
        this.renderer.removeClass(linkBtn, "custom-paginator-page-enabled");
        this.renderer.addClass(linkBtn, "custom-paginator-page-disabled");
        break;
      case this.pageGapTxt[0]: // forward "..."
        this.renderer.listen(linkBtn, "click", () => {
          this.switchPage(
            this.currentPage < this.showTotalPages + 1
              ? this.showTotalPages + 2
              : this.currentPage + this.showTotalPages - 1
          );
        });
        break;
      case this.pageGapTxt[1]: // backward "..."
        this.renderer.listen(linkBtn, "click", () => {
          this.switchPage(
            this.currentPage >
              this.matPag.getNumberOfPages() - this.showTotalPages - 2
              ? this.matPag.getNumberOfPages() - this.showTotalPages - 3
              : this.currentPage - this.showTotalPages + 1
          );
        });
        break;
      default:
        this.renderer.listen(linkBtn, "click", () => {
          this.switchPage(+index);
        });
        break;
    }

    this.renderer.appendChild(linkBtn, text);
    this.buttons.push(linkBtn);
    return linkBtn;
  }

  /**
   * @description calculates the button range based on class input parameters and current page index.
   */
  private initPageRange(): void {
    this.rangeStart = this.currentPage - this.showTotalPages / 2;
    this.rangeEnd = this.currentPage + this.showTotalPages / 2;
    this.buildPageNumbers();
  }

  private switchPage(index: number): void {
    this.matPag.pageIndex = index;
    this.matPag.page.emit({
      previousPageIndex: this.currentPage,
      pageIndex: index,
      pageSize: this.matPag.pageSize,
      length: this.matPag.length,
    });
    this.currentPage = index;
    this.initPageRange();
  }
}
